import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts';
import { ArrowRightIcon, RotateCcwIcon } from 'lucide-react';
import type { AdminPageProps } from '../../types/navigation';
import { useStore } from '../../contexts/StoreContext';
import { KPI_BASE, agingBaseline, approvalDays, categoryBaseline, divisionBaseline, monthlyCollection, monthlySeizures, reasonBaseline, stationBaseline } from '../../data/charts';
import { PageHeader } from '../../components/admin/PageHeader';
import { ChartPanel, CHART } from '../../components/admin/ChartPanel';
import { DhakaMap } from '../../components/shared/DhakaMap';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { isPaid, needsVerification, stageInfo } from '../../utils/caseStatus';
import { bn, daysBetween, fmtDate, isDemoToday, taka } from '../../utils/bn';
const STATUS_FILTERS = [{
  id: 'all',
  label: 'সকল অবস্থা'
}, {
  id: 'active',
  label: 'চলমান'
}, {
  id: 'at_station',
  label: 'স্টেশনে রয়েছে'
}, {
  id: 'released',
  label: 'অবমুক্ত'
}];
const shortDiv = (d: string) => d.replace('ট্রাফিক-', '').replace(' বিভাগ', '');
const fmtK = (n: number) => bn(n >= 100000 ? `${(n / 100000).toFixed(1)} লাখ` : n.toLocaleString('en-IN'));
export function Dashboard({
  go,
  openCase
}: AdminPageProps) {
  const {
    cases,
    config
  } = useStore();
  const [range, setRange] = useState('6');
  const [division, setDivision] = useState('');
  const [station, setStation] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('all');
  const [selected, setSelected] = useState<string | undefined>();
  const filtered = useMemo(() => cases.filter(c => (!division || c.division === division) && (!station || (c.station?.station ?? c.assignedStation) === station) && (!category || c.category === category) && (status === 'all' || (status === 'active' ? c.stage !== 'released' : c.stage === status))), [cases, division, station, category, status]);
  const live = {
    atStation: cases.filter(c => c.stage === 'at_station').length,
    today: cases.filter(c => isDemoToday(c.createdAt)).length,
    releasedToday: cases.filter(c => c.stage === 'released' && isDemoToday(c.letter?.redeemedAt)).length,
    collected: cases.filter(isPaid).reduce((s, c) => s + c.fine + c.extraCharge, 0),
    long: cases.filter(c => c.stage === 'at_station' && daysBetween(c.createdAt) > 7).length
  };
  const totalAtStation = Object.values(stationBaseline).reduce((a, b) => a + b, 0) + live.atStation;
  const queues = [{
    label: 'কাগজপত্র যাচাই অপেক্ষমাণ',
    value: filtered.filter(needsVerification).length,
    page: 'verification' as const,
    tone: 'text-warning-700'
  }, {
    label: 'পরিশোধ অপেক্ষমাণ',
    value: filtered.filter(c => c.stage === 'at_station' && !isPaid(c)).length,
    page: 'payments' as const,
    tone: 'text-warning-700'
  }, {
    label: 'অনুমোদন অপেক্ষমাণ',
    value: filtered.filter(c => c.approval === 'pending').length,
    page: 'notesheet' as const,
    tone: 'text-navy'
  }, {
    label: 'অবমুক্তির অনুমোদনপ্রাপ্ত',
    value: filtered.filter(c => c.letter?.status === 'issued').length,
    page: 'letters' as const,
    tone: 'text-success'
  }];
  const months = monthlySeizures.slice(-Number(range));
  const collection = monthlyCollection.slice(-Number(range));
  const catData = config.categories.map(c => ({
    name: c.label,
    key: c.key,
    value: categoryBaseline[c.key] + cases.filter(x => x.category === c.key).length
  }));
  const reasonData = Object.entries(reasonBaseline).map(([k, v]) => ({
    name: k.split(' / ')[0],
    value: v + cases.filter(c => c.reason === k).length
  })).sort((a, b) => b.value - a.value);
  const divData = Object.entries(divisionBaseline).map(([k, v]) => ({
    name: shortDiv(k),
    full: k,
    value: v + cases.filter(c => c.division === k).length
  }));
  const stationData = config.stations.filter(s => !station || s.name === station).map(s => ({
    name: s.short,
    full: s.name,
    value: stationBaseline[s.name] + cases.filter(c => c.stage === 'at_station' && c.station?.station === s.name).length,
    capacity: s.capacity
  }));
  const aging = agingBaseline.map((a, i) => ({
    ...a,
    count: a.count + (i === 0 ? live.atStation : 0)
  }));
  const markers = [...filtered.filter(c => c.stage !== 'released').map(c => ({
    id: c.pcr,
    x: c.location.x,
    y: c.location.y,
    label: c.pcr,
    kind: 'custody' as const
  })), ...config.stations.map(s => ({
    id: s.name,
    x: s.location.x,
    y: s.location.y,
    label: s.short,
    kind: 'station' as const,
    count: stationData.find(d => d.full === s.name)?.value ?? stationBaseline[s.name]
  }))];
  const sel = cases.find(c => c.pcr === selected);
  const reset = () => {
    setDivision('');
    setStation('');
    setCategory('');
    setStatus('all');
    setRange('6');
  };
  return <>
      <PageHeader title="নির্বাহী ড্যাশবোর্ড" crumbs={[]} description="ডিএমপি জুড়ে যানবাহন হেফাজত, অবমুক্তি ও আদায়ের সার্বিক চিত্র" data-id="element-1010" data-mp="mh2kljrib4u7" />

      <div className="card mb-5 flex flex-wrap items-end gap-3 p-3" data-id="element-1011" data-mp="2y57i7b43v690">
        <Filter label="সময়কাল" value={range} onChange={setRange} options={[['3', 'শেষ ৩ মাস'], ['6', 'শেষ ৬ মাস']]} data-id="element-1012" data-mp="325htn3ao2ov9" />
        <Filter label="ট্রাফিক বিভাগ" value={division} onChange={setDivision} options={[['', 'সকল বিভাগ'], ...config.divisions.map(d => [d.name, shortDiv(d.name)] as [string, string])]} data-id="element-1013" data-mp="374m7464mj8re" />
        <Filter label="ডাম্পিং স্টেশন" value={station} onChange={setStation} options={[['', 'সকল স্টেশন'], ...config.stations.map(s => [s.name, s.short] as [string, string])]} data-id="element-1014" data-mp="3gvgrf4yb7ya7" />
        <Filter label="যানবাহনের ধরন" value={category} onChange={setCategory} options={[['', 'সকল ধরন'], ...config.categories.map(c => [c.key, c.label] as [string, string])]} data-id="element-1015" data-mp="3pvlmqgq8b680" />
        <Filter label="অবস্থা" value={status} onChange={setStatus} options={STATUS_FILTERS.map(s => [s.id, s.label] as [string, string])} data-id="element-1016" data-mp="3us2ncaem7d1z" />
        <button className="btn btn-ghost btn-sm" onClick={reset} data-id="element-1017" data-mp="20fm9anfkuumk"><RotateCcwIcon className="h-3.5 w-3.5" data-id="element-1018" data-mp="20i8rdh03gsj1" /> রিসেট</button>
      </div>

      <section className="mb-5 grid gap-5 xl:grid-cols-[1.35fr_1fr]" data-id="element-1019" data-mp="33ruwxaswjcl1">
        <div className="card overflow-hidden" data-id="element-1020" data-mp="2yt9yngx7n2z8">
          <div className="grid grid-cols-2 md:grid-cols-3 divide-x divide-y divide-line" data-id="element-1021" data-mp="2yub7rfuo1feb">
            <Stat big label="মোট আটককৃত যানবাহন (২০২৬)" value={bn((KPI_BASE.totalSeized + cases.length).toLocaleString('en-IN'))} data-id="element-1022" data-mp="3cpwv7il93r4m" />
            <Stat big label="বর্তমানে ডাম্পিংয়ে রয়েছে" value={bn(totalAtStation)} note={`ধারণক্ষমতা ${bn(config.stations.reduce((s, x) => s + x.capacity, 0))}`} data-id="element-1023" data-mp="3mpxkdl5sx1gq" />
            <Stat big label="মোট আদায়কৃত অর্থ" value={`৳${fmtK(KPI_BASE.collected + live.collected)}`} note="নমুনা পরিমাণ" data-id="element-1024" data-mp="3hp0278ksk7f8" />
            <Stat label="আজকের নতুন PCR" value={bn(KPI_BASE.todayNew + live.today)} data-id="element-1025" data-mp="3rgvz4gb55rhv" />
            <Stat label="আজকের অবমুক্তি" value={bn(KPI_BASE.todayReleased + live.releasedToday)} data-id="element-1026" data-mp="3rj9vqtlgzbrj" />
            <Stat label="দীর্ঘদিন ধরে অনিষ্পন্ন (৩০+ দিন)" value={bn(KPI_BASE.longPending + live.long)} tone="text-danger" onClick={() => go('reports')} data-id="element-1027" data-mp="98y62u2ydcja" />
          </div>
        </div>
        <div className="card p-4" data-id="element-1028" data-mp="nyrkan8aq3ul">
          <div className="mb-2 flex items-center justify-between" data-id="element-1029" data-mp="swsyatqv7cz7">
            <h2 className="text-sm font-bold text-ink" data-id="element-1030" data-mp="sv01s79yu94x">চলমান কার্যপ্রবাহ</h2>
            <span className="text-[11px] text-ink-soft" data-id="element-1031" data-mp="dzac9z5kndvg">ডেমো কেস রেকর্ড · লাইভ</span>
          </div>
          <ul className="divide-y divide-line" data-id="element-1032" data-mp="ixbu6gr9h75c">
            {queues.map(q => <li key={q.label} data-id="element-1033" data-mp="56grxnb9poaq">
                <button onClick={() => go(q.page)} className="group flex w-full items-center justify-between py-3 text-left" data-id="element-1034" data-mp="55f6cowmljer">
                  <span className="text-sm text-ink" data-id="element-1035" data-mp="3reykgjri5iym">{q.label}</span>
                  <span className="flex items-center gap-3" data-id="element-1036" data-mp="3rcltjh3rny9f">
                    <span className={`text-2xl font-bold ${q.tone}`} data-id="element-1037" data-mp="9gn1fq5mrl7">{bn(q.value)}</span>
                    <ArrowRightIcon className="h-4 w-4 text-ink-soft transition-transform duration-150 group-hover:translate-x-0.5" data-id="element-1038" data-mp="3ids2e697g039" />
                  </span>
                </button>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="mb-5 grid gap-5 xl:grid-cols-[1fr_1fr]" data-id="element-1039" data-mp="6ne5beh2di3u">
        <ChartPanel title="ঢাকা মহানগর — হেফাজত ও ডাম্পিং স্টেশন" subtitle={division ? `নির্বাচিত: ${division}` : 'লাল: হেফাজত স্থান · সবুজ: স্টেশন (যানবাহন সংখ্যা)'} data-id="element-1040" data-mp="1r6wndxa9si7">
          <DhakaMap className="h-[340px]" markers={markers} selectedId={selected} onSelect={setSelected} highlightArea={division ? shortDiv(division) : undefined} data-id="element-1041" data-mp="3nyn9ttpgyeik" />
          {sel && <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-canvas p-3" data-id="element-1042" data-mp="3f5topfncckws">
              <div className="min-w-0" data-id="element-1043" data-mp="3a5xn1n2495ke">
                <p className="font-mono text-sm font-bold text-navy" data-id="element-1044" data-mp="3a6yy3rj4ts24">{sel.pcr}</p>
                <p className="truncate text-xs text-ink-muted" data-id="element-1045" data-mp="358faznssya86">{sel.regNo} · {sel.road} · {fmtDate(sel.createdAt)}</p>
              </div>
              <StatusBadge label={stageInfo(sel).label} tone={stageInfo(sel).tone} size="sm" data-id="element-1046" data-mp="30adrx2vdmawp" />
              <button className="btn btn-sm btn-outline" onClick={() => openCase(sel.pcr)} data-id="element-1047" data-mp="2w8sl9z9fqsuy">খুলুন</button>
            </div>}
        </ChartPanel>
        <ChartPanel title="মাসভিত্তিক যানবাহন আটক ও অবমুক্তি" subtitle="অক্টোবর: চলতি মাস (আংশিক)" data-id="element-1048" data-mp="1fph2rf2n2dew">
          <div className="h-[340px]" data-id="element-1049" data-mp="1kp24fx970zvb">
            <ResponsiveContainer data-id="element-1050" data-mp="1kq4r1szhgg5f">
              <BarChart data={months} barGap={2} data-id="element-1051" data-mp="1po6cgkkdsrk2">
                <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-1052" data-mp="1pnwlouriigdb" />
                <XAxis dataKey="month" tick={CHART.tick} axisLine={false} tickLine={false} data-id="element-1053" data-mp="s1fpxtyfgato" />
                <YAxis tick={CHART.tick} axisLine={false} tickLine={false} tickFormatter={v => bn(v)} data-id="element-1054" data-mp="wz7oh6tkuv6u" />
                <Tooltip contentStyle={CHART.tooltip} formatter={(v: number) => bn(v)} data-id="element-1055" data-mp="11ykbbrkg829f" />
                <Legend wrapperStyle={{
                fontFamily: 'Hind Siliguri',
                fontSize: 12
              }} data-id="element-1056" data-mp="16q2hcu1bwt19" />
                <Bar dataKey="seized" name="আটক" fill={CHART.navy} radius={[4, 4, 0, 0]} data-id="element-1057" data-mp="9ag4h2q55j55" />
                <Bar dataKey="released" name="অবমুক্ত" fill={CHART.green} radius={[4, 4, 0, 0]} data-id="element-1058" data-mp="e99kb1swhm8h" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartPanel>
      </section>

      <section className="grid gap-5 lg:grid-cols-2 xl:grid-cols-3" data-id="element-1059" data-mp="1pfabg1ei23n">
        <ChartPanel title="যানবাহনের ধরন অনুযায়ী আটক" data-id="element-1060" data-mp="1rs0uhl85ksb">
          <div className="h-56" data-id="element-1061" data-mp="6ptjx2inhk3s"><ResponsiveContainer data-id="element-1062" data-mp="6pbdxe66rrio">
            <BarChart data={catData} data-id="element-1063" data-mp="6mxj96ff4hbn">
              <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-1064" data-mp="bo4rf6lh3iiu" />
              <XAxis dataKey="name" tick={{
                ...CHART.tick,
                fontSize: 10
              }} interval={0} axisLine={false} tickLine={false} data-id="element-1065" data-mp="3a5ilvwvmwax9" />
              <YAxis tick={CHART.tick} axisLine={false} tickLine={false} tickFormatter={v => bn(v)} width={36} data-id="element-1066" data-mp="3f3sfw45mh5ha" />
              <Tooltip contentStyle={CHART.tooltip} formatter={(v: number) => bn(v)} data-id="element-1067" data-mp="3k2n4xnu6nq9u" />
              <Bar dataKey="value" name="আটক" radius={[4, 4, 0, 0]} data-id="element-1068" data-mp="3oumzt1vebszk">
                {catData.map(d => <Cell key={d.key} fill={!category || category === d.key ? CHART.navy : CHART.sky} data-id="element-1069" data-mp="2rfizjhr6h9xj" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer></div>
        </ChartPanel>
        <ChartPanel title="কারণভিত্তিক আটক" data-id="element-1070" data-mp="1djhjcyd6v08v">
          <div className="h-56" data-id="element-1071" data-mp="1di6hx577u4kx"><ResponsiveContainer data-id="element-1072" data-mp="1dgn1nelk7tj5">
            <BarChart data={reasonData} layout="vertical" margin={{
              left: 8
            }} data-id="element-1073" data-mp="19h4h4jxnhkgu">
              <XAxis type="number" hide data-id="element-1074" data-mp="14i1aiv20v66r" />
              <YAxis type="category" dataKey="name" tick={{
                ...CHART.tick,
                fontSize: 10
              }} width={128} axisLine={false} tickLine={false} data-id="element-1075" data-mp="14gzwqitmg5lc" />
              <Tooltip contentStyle={CHART.tooltip} formatter={(v: number) => bn(v)} data-id="element-1076" data-mp="zlkl7xj36ls3" />
              <Bar dataKey="value" name="আটক" fill={CHART.green} radius={[0, 4, 4, 0]} barSize={12} data-id="element-1077" data-mp="um83fr72o4wd" />
            </BarChart>
          </ResponsiveContainer></div>
        </ChartPanel>
        <ChartPanel title="বিভাগভিত্তিক PCR" subtitle="বারে ক্লিক করে বিভাগ নির্বাচন করুন" data-id="element-1078" data-mp="lsbfc6uckieb">
          <div className="h-56" data-id="element-1079" data-mp="gu9zdus7wz72"><ResponsiveContainer data-id="element-1080" data-mp="gqm52tey88ra">
            <BarChart data={divData} data-id="element-1081" data-mp="btchzkf1896m">
              <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-1082" data-mp="bsawsn4ay6b6" />
              <XAxis dataKey="name" tick={{
                ...CHART.tick,
                fontSize: 10
              }} interval={0} axisLine={false} tickLine={false} data-id="element-1083" data-mp="6u10g96ucznz" />
              <YAxis tick={CHART.tick} axisLine={false} tickLine={false} tickFormatter={v => bn(v)} width={36} data-id="element-1084" data-mp="3tbjh16dnr2cb" />
              <Tooltip contentStyle={CHART.tooltip} formatter={(v: number) => bn(v)} data-id="element-1085" data-mp="27l11w9pcs29" />
              <Bar dataKey="value" name="PCR" radius={[4, 4, 0, 0]} cursor="pointer" onClick={d => {
                const full = (d as unknown as {
                  full: string;
                }).full;
                setDivision(division === full ? '' : full);
              }} data-id="element-1086" data-mp="3kcy0thvnrzep">
                {divData.map(d => <Cell key={d.full} fill={!division || division === d.full ? CHART.navy : CHART.sky} data-id="element-1087" data-mp="fzhqttnwepvh" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer></div>
        </ChartPanel>
        <ChartPanel title="মাসভিত্তিক জরিমানা আদায়" subtitle="নমুনা পরিমাণ (৳)" data-id="element-1088" data-mp="730bsfcey7ug">
          <div className="h-56" data-id="element-1089" data-mp="b551j2zqqkbt"><ResponsiveContainer data-id="element-1090" data-mp="b4btzzsyjqsm">
            <LineChart data={collection} data-id="element-1091" data-mp="yvqz20c4cm7k">
              <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-1092" data-mp="yuplnp7mrnmo" />
              <XAxis dataKey="month" tick={CHART.tick} axisLine={false} tickLine={false} data-id="element-1093" data-mp="13n900oe56msj" />
              <YAxis tick={CHART.tick} axisLine={false} tickLine={false} tickFormatter={v => fmtK(v)} width={52} data-id="element-1094" data-mp="2ocwenxoi7zbu" />
              <Tooltip contentStyle={CHART.tooltip} formatter={(v: number) => taka(v)} data-id="element-1095" data-mp="2jda1fo8oej31" />
              <Line type="monotone" dataKey="amount" name="আদায়" stroke={CHART.navy} strokeWidth={2} dot={{
                r: 3
              }} data-id="element-1096" data-mp="2y3h8l7t8gfof" />
              <Line type="monotone" dataKey="reconciled" name="সমন্বিত" stroke={CHART.green} strokeWidth={2} dot={{
                r: 3
              }} data-id="element-1097" data-mp="2t5fnyig5s8au" />
            </LineChart>
          </ResponsiveContainer></div>
        </ChartPanel>
        <ChartPanel title="ডাম্পিং স্টেশনভিত্তিক অবস্থান" subtitle="বর্তমান যানবাহন বনাম ধারণক্ষমতা" data-id="element-1098" data-mp="20h0dm97z2e5u">
          <ul className="space-y-3 pt-1" data-id="element-1099" data-mp="2feau5wzh2a8f">
            {stationData.map(s => {
            const pct = Math.round(s.value / s.capacity * 100);
            return <li key={s.full} data-id="element-1100" data-mp="3qqtvxk77wris">
                  <div className="flex justify-between text-xs" data-id="element-1101" data-mp="3qtzk8x80vpdg"><span className="font-semibold text-ink" data-id="element-1102" data-mp="3ltbk9m1tfy73">{s.full}</span><span className="text-ink-muted" data-id="element-1103" data-mp="3luwf7cs1lj5h">{bn(s.value)} / {bn(s.capacity)} · {bn(pct)}%</span></div>
                  <div className="mt-1 h-2.5 rounded-full bg-canvas" data-id="element-1104" data-mp="1ve15l383yzyg"><div className={`h-2.5 rounded-full ${pct > 85 ? 'bg-danger' : pct > 65 ? 'bg-warning' : 'bg-bdgreen'}`} style={{
                  width: `${Math.min(100, pct)}%`
                }} data-id="element-1105" data-mp="20bsvxst3ue0j" /></div>
                </li>;
          })}
          </ul>
        </ChartPanel>
        <ChartPanel title="অনুমোদনে গড় সময় (দিন)" data-id="element-1106" data-mp="2eak9ne421xo5">
          <div className="h-56" data-id="element-1107" data-mp="2e9r76pc4pubt"><ResponsiveContainer data-id="element-1108" data-mp="2e8g6wzhw74pg">
            <BarChart data={approvalDays} data-id="element-1109" data-mp="2j9dp3c2wjyqy">
              <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-1110" data-mp="2j8c8yt714m2d" />
              <XAxis dataKey="division" tick={{
                ...CHART.tick,
                fontSize: 10
              }} interval={0} axisLine={false} tickLine={false} data-id="element-1111" data-mp="2nzu3oziglqew" />
              <YAxis tick={CHART.tick} axisLine={false} tickLine={false} tickFormatter={v => bn(v)} width={28} data-id="element-1112" data-mp="2s0nr7rrzp379" />
              <Tooltip contentStyle={CHART.tooltip} formatter={(v: number) => `${bn(v)} দিন`} data-id="element-1113" data-mp="2x2v7gtjur5fe" />
              <Bar dataKey="days" name="গড় দিন" radius={[4, 4, 0, 0]} data-id="element-1114" data-mp="320ek5p0wja58">
                {approvalDays.map(d => <Cell key={d.division} fill={d.days > 4 ? CHART.warning : CHART.navy} data-id="element-1115" data-mp="3c5ms3j8rs6uk" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer></div>
        </ChartPanel>
        <ChartPanel title="দীর্ঘদিনের অনিষ্পন্ন মামলা" subtitle="স্টেশনে অবস্থানকাল অনুযায়ী" className="lg:col-span-2 xl:col-span-3" data-id="element-1116" data-mp="326nncbr4kvcu">
          <div className="h-48" data-id="element-1117" data-mp="2y6n84ow7cg4q"><ResponsiveContainer data-id="element-1118" data-mp="3uu9yq66lh4a9">
            <BarChart data={aging} data-id="element-1119" data-mp="3ut8b728e6pbn">
              <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-1120" data-mp="3uwnuyid0gmje" />
              <XAxis dataKey="bucket" tick={CHART.tick} axisLine={false} tickLine={false} data-id="element-1121" data-mp="3pwrqki0eit3b" />
              <YAxis tick={CHART.tick} axisLine={false} tickLine={false} tickFormatter={v => bn(v)} width={32} data-id="element-1122" data-mp="3l3fch1tcm4ot" />
              <Tooltip contentStyle={CHART.tooltip} formatter={(v: number) => bn(v)} data-id="element-1123" data-mp="3h2n03zp6jjob" />
              <Bar dataKey="count" name="যানবাহন" radius={[4, 4, 0, 0]} data-id="element-1124" data-mp="hggcn37o8xcr">
                {aging.map((a, i) => <Cell key={a.bucket} fill={i >= 3 ? CHART.danger : i === 2 ? CHART.warning : CHART.navy} data-id="element-1125" data-mp="co51f47hdp4w" data-mpi={i} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer></div>
        </ChartPanel>
      </section>
    </>;
}
function Stat({
  label,
  value,
  note,
  big,
  tone,
  onClick
}: {
  label: string;
  value: string;
  note?: string;
  big?: boolean;
  tone?: string;
  onClick?: () => void;
}) {
  const Tag = onClick ? 'button' : 'div';
  return <Tag onClick={onClick} className={`p-4 text-left ${onClick ? 'hover:bg-navy-50' : ''}`} data-id="element-1126" data-mp="1o6anpglerz6o">
      <p className="text-xs text-ink-muted" data-id="element-1127" data-mp="1o8nf0kd23lwe">{label}</p>
      <p className={`${big ? 'text-3xl' : 'text-2xl'} font-bold ${tone ?? 'text-navy'}`} data-id="element-1128" data-mp="1t65gjpoquyud">{value}</p>
      {note && <p className="text-[11px] text-ink-soft" data-id="element-1129" data-mp="1e9o4ew8qdg6r">{note}</p>}
    </Tag>;
}
function Filter({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return <label className="flex min-w-[140px] flex-col text-xs font-semibold text-ink-muted" data-id="element-1130" data-mp="38kb7wsu1au8w">
      {label}
      <select className="input mt-1 h-9" value={value} onChange={e => onChange(e.target.value)} data-id="element-1131" data-mp="11xvjebl3kwfj">
        {options.map(([v, l]) => <option key={v} value={v} data-id="element-1132" data-mp="wzk7zwf5mdv7">{l}</option>)}
      </select>
    </label>;
}